import React, { useState, useEffect } from 'react';
import { Download, Search, Trash2, Plus, Edit2, Paperclip, CheckSquare, Square, GripVertical, Settings, RefreshCw, Archive, Eye, EyeOff, Bot } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import api, { authUrl } from '../api';
import { formatEUR } from '../utils/format';
import { MonthContext } from '../context/MonthContext';

const DEFAULT_COLS = [
  { id: 'select', label: '☑', width: '40px' },
  { id: 'actions', label: 'Actions' },
  { id: 'source', label: 'Source' },
  { id: 'processed', label: 'Traité', type: 'boolean' },
  { id: 'id', label: 'ID' },
  { id: 'timestamp', label: 'Chargé le', type: 'date' },
  { id: 'date', label: 'Date Facture', type: 'date' },
  { id: 'fournisseur', label: 'Fournisseur' },
  { id: 'computed_expense_type_id', label: 'Type dépense' },
  { id: 'montant_ttc', label: 'TTC', type: 'number' },
  { id: 'montant_ht', label: 'HT', type: 'number' },
  { id: 'tva_55', label: 'TVA 5.5', type: 'number' },
  { id: 'tva_10', label: 'TVA 10', type: 'number' },
  { id: 'tva_20', label: 'TVA 20', type: 'number' },
  { id: 'currency', label: 'Devise' },
  { id: 'is_expense_note', label: 'NdF', type: 'boolean' },
  { id: 'is_client_rebill', label: 'Refact.', type: 'boolean' },
  { id: 'project', label: 'Projet' },
  { id: 'axonaut_order_id', label: 'Commande' },
  { id: 'axonaut_message', label: 'Msg. Axonaut' },
  { id: 'comment', label: 'Commentaire' },
  { id: 'status', label: 'Statut', type: 'select', options: ['SUCCES', 'SUCCES_MAIL', 'ERREUR', 'NON_TRANSMIS', 'MANUEL', 'EN COURS'] }
];

const translateAxonautMessage = (msg) => {
  if (!msg) return '-';
  if (msg.includes('Reçu de')) return '-'; // Message is the email source
  
  const lower = msg.toLowerCase();
  if (lower.includes('duplicate')) return 'Facture déjà existante (doublon)';
  if (lower.includes('fournisseur inconnu') || lower.includes('company not found') || lower.includes('supplier')) return 'Fournisseur introuvable ou mal identifié';
  if (lower.includes('tva') || lower.includes('tax')) return 'Problème de TVA (montant ou taux non reconnu)';
  if (lower.includes('amount') || lower.includes('montant') || lower.includes('total mismatch') || lower.includes('sum of parts')) return 'Le montant calculé ne correspond pas au total (Écart)';
  if (lower.includes('currency') || lower.includes('devise')) return 'Devise non supportée';
  if (lower.includes('category') || lower.includes('type')) return 'Catégorie de dépense non reconnue';
  if (lower.includes('missing attachment') || lower.includes('pièce jointe')) return 'Pièce jointe manquante ou illisible';
  if (/\b(réussi|succes|succès|ok)\b/i.test(msg) && !lower.includes('unsuccessful')) return 'Transmis avec succès 🎉';
  
  // Clean json objects
  if (msg.startsWith('{') || msg.includes('"error"')) return 'Alerte technique à vérifier (voir détail JSON)';
  
  return msg;
};

const SortableHeader = ({ col, sortBy, sortDir, filters, setFilters, handleSort, setPage, allSelected, handleSelectAll, width, onResize }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: col.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    width: width || col.width || '150px',
    minWidth: width || col.width || '150px',
    maxWidth: width || col.width || '150px',
    padding: 0,
    verticalAlign: 'top',
    background: 'var(--surface)',
    borderBottom: '2px solid var(--border)',
    zIndex: transform ? 1 : 0,
    position: 'relative'
  };

  const handleFilterChange = (e) => {
    setFilters(prev => ({ ...prev, [col.id]: e.target.value }));
    setPage(1);
  };

  const handleResizeMouseDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const startX = e.pageX;
    const startWidth = parseInt(width || col.width || '150', 10);
    
    const handleMouseMove = (moveEvent) => {
      const newWidth = Math.max(50, startWidth + (moveEvent.pageX - startX));
      onResize(`${newWidth}px`);
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <th ref={setNodeRef} style={style}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', borderBottom: '1px solid var(--glass-border)', minHeight: '48px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', flexGrow: 1 }} onClick={() => handleSort(col.id)}>
          {col.id === 'select' ? (
            <input 
              type="checkbox" 
              checked={allSelected} 
              onChange={handleSelectAll} 
              onClick={(e) => e.stopPropagation()} 
            />
          ) : (
            <>
              {col.label} {sortBy === col.id && (sortDir === 'asc' ? '↑' : '↓')}
            </>
          )}
        </div>
        {col.id !== 'select' && col.id !== 'actions' && (
          <div {...attributes} {...listeners} style={{ cursor: 'grab', padding: '2px', opacity: 0.5 }}>
            <GripVertical size={14} />
          </div>
        )}
      </div>
      <div style={{ padding: '0.5rem 1rem', minHeight: '52px', display: 'flex', alignItems: 'center', background: 'var(--bg-secondary)' }}>
        {col.id !== 'select' && col.id !== 'actions' && col.type !== 'boolean' && col.type !== 'select' && (
          <input 
            type="text" 
            className="input-field" 
            style={{ padding: '0.35rem 0.5rem', fontSize: '0.75rem', width: '100%', borderRadius: '6px' }} 
            placeholder={col.type === 'date' ? ">01/10/24 ou Entre X et Y" : "Filtrer..."} 
            value={filters[col.id] || ''} 
            onChange={handleFilterChange} 
          />
        )}
        {col.type === 'select' && (
          <select className="input-field" style={{ padding: '0.35rem 0.5rem', fontSize: '0.75rem', width: '100%', borderRadius: '6px' }} value={filters[col.id] || ''} onChange={handleFilterChange}>
            <option value="">Tous</option>
            {col.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        )}
      </div>
      <div 
        onMouseDown={handleResizeMouseDown}
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: '6px',
          cursor: 'col-resize',
          zIndex: 10,
          background: 'transparent'
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--accent)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
      />
    </th>
  );
};

const getStatusStyle = (status) => {
  if (!status) return { background: '#e0e0e0', color: '#1a1a1a', label: 'Inconnu' };
  const s = status.toUpperCase();
  if (s === 'SUCCES') return { background: 'rgba(16, 185, 129, 0.15)', color: '#059669', label: 'Succès' };
  if (s === 'SUCCES_MAIL') return { background: 'rgba(56, 189, 248, 0.15)', color: '#0284c7', label: 'Succès (Mail)' };
  if (s === 'ERREUR') return { background: 'rgba(239, 68, 68, 0.15)', color: '#dc2626', label: 'Erreur' };
  if (s === 'EXTRACTION_IA') return { background: 'rgba(168, 85, 247, 0.15)', color: '#9333ea', label: 'Extraction IA...' };
  if (s === 'IA_TERMINEE') return { background: 'rgba(245, 158, 11, 0.15)', color: '#d97706', label: 'IA Terminée' };
  if (s === 'DOUBLON') return { background: 'rgba(100, 116, 139, 0.15)', color: '#475569', label: 'Doublon' };
  if (s === 'MANUEL') return { background: 'rgba(59, 130, 246, 0.15)', color: '#2563eb', label: 'Manuel' };
  if (s === 'NON_TRANSMIS') return { background: 'rgba(245, 158, 11, 0.15)', color: '#b45309', label: 'Non transmis (à lancer)' };
  return { background: '#e0e0e0', color: '#1a1a1a', label: status };
};

const getCellTitle = (col, row) => {
  if (col.id === 'select' || col.id === 'actions') return undefined;
  if (col.id === 'axonaut_message') return translateAxonautMessage(row.message) + (row.message ? `\n\nDétail technique : ${row.message}` : '');
  if (col.id === 'computed_expense_type_id') return row.computed_expense_type_id ? `${row.computed_expense_type_id} - ${row.computed_expense_description || ''}` : undefined;
  if (col.id === 'source') {
      const isEmail = row.pdf_path || (row.message && row.message.includes('Reçu de'));
      if (row.qonto_transaction_id) return 'Qonto';
      if (isEmail) return 'Email';
      return 'N/A';
  }
  const val = row[col.id];
  return (typeof val === 'string' || typeof val === 'number') ? val : undefined;
};

const HistoryTable = () => {
  const { selectedMonth } = React.useContext(MonthContext);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  
  const [page, setPage] = useState(1);
  const limit = 50; 
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('id');
  const [sortDir, setSortDir] = useState('desc');
  
  const [filters, setFilters] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [jsonViewer, setJsonViewer] = useState(null);
  const [hideProcessed, setHideProcessed] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // Column management
  const [activeCols, setActiveCols] = useState(() => {
    const saved = localStorage.getItem('history_cols_v5');
    return saved ? JSON.parse(saved) : DEFAULT_COLS.map(c => c.id);
  });

  const [colWidths, setColWidths] = useState(() => {
    const saved = localStorage.getItem('history_col_widths_v5');
    return saved ? JSON.parse(saved) : {};
  });

  const handleResizeColumn = (colId, newWidth) => {
    setColWidths(prev => {
      const next = { ...prev, [colId]: newWidth };
      localStorage.setItem('history_col_widths_v5', JSON.stringify(next));
      return next;
    });
  };

  const columns = activeCols.map(id => DEFAULT_COLS.find(c => c.id === id)).filter(Boolean);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setActiveCols((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        const newArray = arrayMove(items, oldIndex, newIndex);
        localStorage.setItem('history_cols_v5', JSON.stringify(newArray));
        return newArray;
      });
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page, limit, search, sortBy, sortDir, is_archived: 'all', hideProcessed: hideProcessed ? 'true' : 'false', month_year: selectedMonth === 'all' ? '' : selectedMonth, ...filters
      });
      const res = await api.get(`/expenses?${params.toString()}`);
      setData(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    const timer = setTimeout(() => {
      loadData(); 
    }, 300);
    return () => clearTimeout(timer);
  }, [page, search, sortBy, sortDir, filters, hideProcessed, selectedMonth]);

  const handleToggleProcessed = async (status) => {
    if (selectedIds.length === 0) return;
    setProcessing(true);
    try {
        await api.post('/expenses/bulk-processed', { ids: selectedIds, processed: status });
        setSelectedIds([]);
        loadData();
    } catch (err) {
        console.error("Erreur de mise à jour du statut Traité:", err);
    } finally {
        setProcessing(false);
    }
  };

  const handleQuickToggleProcessed = async (id, status) => {
    try {
        await api.post('/expenses/bulk-processed', { ids: [id], processed: status });
        loadData();
    } catch (err) {
        console.error("Erreur de mise à jour rapide du statut Traité:", err);
    }
  };

  const handleSort = (field) => {
    if (field === 'select' || field === 'actions') return;
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedIds(data.map(d => d.id));
    else setSelectedIds([]);
  };

  const handleSelect = (id) => {
    if (selectedIds.includes(id)) setSelectedIds(selectedIds.filter(i => i !== id));
    else setSelectedIds([...selectedIds, id]);
  };

  const handleDeleteMany = async () => {
    if (!window.confirm(`Supprimer ${selectedIds.length} facture(s) ?`)) return;
    try {
      await api.delete('/expenses', { data: { ids: selectedIds } });
      setSelectedIds([]);
      loadData();
    } catch (err) {
      alert("Erreur lors de la suppression.");
    }
  };


  const handleSelectAllGlobally = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: 100000, search, sortBy, sortDir, is_archived: 'all', ...filters
      });
      const res = await api.get(`/expenses?${params.toString()}`);
      if (res.data && res.data.data) {
        setSelectedIds(res.data.data.map(d => d.id));
      }
    } catch(e) {
      alert("Erreur de sélection globale");
    } finally {
      setLoading(false);
    }
  };

  const handleRetryAxonaut = async (id) => {
    try {
      setLoading(true);
      await api.post(`/expenses/${id}/retry-axonaut`);
      loadData();
    } catch (e) {
      alert("Erreur de renvoi Axonaut: " + (e.response?.data?.error || e.message));
      setLoading(false);
    }
  };

  const handleForceQontoSync = async () => {
    try {
      setIsSyncing(true);
      await api.post('/qonto/sync');
      loadData();
    } catch (e) {
      alert("Erreur lors de la synchronisation Qonto: " + (e.response?.data?.error || e.message));
    } finally {
      setIsSyncing(false);
    }
  };

  const exportExcel = () => {
    const params = new URLSearchParams({ search, ...filters });
    api.get(`/expenses/export?${params.toString()}`, { responseType: 'blob' })
      .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'export_factures.xlsx');
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      }).catch(() => alert("Erreur d'exportation"));
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditForm({ ...row });
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/expenses/${id}`, { ...editForm, message: 'Édition manuelle' });
      setEditingId(null);
      loadData();
    } catch (e) {
      alert('Erreur modification');
    }
  };
  
  const handleManualAdd = async () => {
    const f = prompt("Nom du fournisseur :");
    if (!f) return;
    try {
      await api.post('/expenses', { 
        fournisseur: f, status: 'MANUEL', 
        date: new Date().toISOString().split('T')[0] 
      });
      loadData();
    } catch (err) {
      alert("Erreur ajout");
    }
  };

  const handleRetryAI = async (id) => {
    if(!window.confirm("Voulez-vous forcer l'IA à analyser de nouveau cette facture ? Cela prendra environ 1 minute en tâche de fond.")) return;
    try {
      const res = await api.post(`/expenses/${id}/retry-ai`);
      if (res.data && res.data.message) alert(res.data.message);
      loadData();
    } catch (err) {
      alert("Erreur: " + (err.response?.data?.error || err.message));
    }
  };

  const renderCellContent = (col, row, isEditing) => {
    if (col.id === 'select') {
      return <input type="checkbox" checked={selectedIds.includes(row.id)} onChange={() => handleSelect(row.id)} />;
    }
    if (col.id === 'actions') {
      if (isEditing) {
        return (
          <>
            <span className="action-link" style={{ color: 'var(--success)', marginRight: '0.5rem' }} onClick={() => saveEdit(row.id)}>Ok</span>
            <span className="action-link" onClick={() => setEditingId(null)}>X</span>
          </>
        );
      }
      return (
        <div className="action-bar" style={{ display: 'flex', gap: '0.8rem', alignItems: 'center', justifyContent: 'center' }}>
          {row.pdf_path && (
            <a href={authUrl(`/api/pdf?path=${encodeURIComponent(row.pdf_path)}`)} target="_blank" rel="noreferrer" title="Ouvrir pièce jointe" className="action-link" style={{ color: 'var(--text-primary)', display: 'flex', alignItems: 'center' }}>
               <Paperclip size={16} />
            </a>
          )}
          {row.pdf_path && (
            <span className="action-link" onClick={() => handleRetryAI(row.id)} title="Relancer l'extraction IA" style={{ display: 'flex', alignItems: 'center', color: '#8b5cf6' }}>
               <Bot size={16} />
            </span>
          )}
          <span className="action-link" style={{ display: 'flex', alignItems: 'center' }} onClick={() => startEdit(row)} title={(() => {
            if (!row.json_ai) return 'Éditer';
            try { return JSON.stringify(JSON.parse(row.json_ai), null, 2); }
            catch(err) { return 'Invalid JSON'; }
          })()}>
             <Edit2 size={16} />
          </span>
          <span className="action-link" style={{ color: 'var(--brand)', display: 'flex', alignItems: 'center' }} onClick={() => handleRetryAxonaut(row.id)} title="Renvoyer vers Axonaut">
             <img src="https://www.google.com/s2/favicons?domain=axonaut.com&sz=64" alt="Axonaut" style={{ width: 16, height: 16, objectFit: 'contain', opacity: 0.9 }} />
          </span>

          <span className="action-link delete" style={{ display: 'flex', alignItems: 'center' }} onClick={async () => {
             if(!window.confirm('Supprimer ?')) return;
             await api.delete('/expenses', { data: { ids: [row.id] }});
             loadData();
          }}>
             <Trash2 size={16} />
          </span>
        </div>
      );
    }
    
    if (col.id === 'source') {
      const isEmail = row.pdf_path || (row.message && row.message.includes('Reçu de'));
      return (
        <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
          {row.qonto_transaction_id ? <span className="badge" style={{ background: '#00e5ff', color: '#1a1a1a' }}>Qonto</span> : isEmail ? <span className="badge" style={{ background: '#ffa200', color: '#1a1a1a' }}>Email</span> : <span className="badge" style={{ background: '#e0e0e0', color: '#1a1a1a' }}>N/A</span>}
        </div>
      );
    }
    
    if (col.id === 'axonaut_message') {
      return translateAxonautMessage(row.message);
    }
    
    if (isEditing && col.id !== 'id' && col.id !== 'timestamp' && col.id !== 'computed_expense_type_id' && col.id !== 'source' && col.id !== 'axonaut_message') {
      if (col.type === 'boolean') {
        return <input type="checkbox" checked={!!editForm[col.id]} onChange={e => setEditForm({...editForm, [col.id]: e.target.checked ? 1 : 0})} />;
      }
      if (col.type === 'select') {
        return (
          <select className="input-field" value={editForm[col.id] || ''} onChange={e => setEditForm({...editForm, [col.id]: e.target.value})} style={{ padding:'0.25rem' }}>
            {col.options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        );
      }
      return <input className="input-field" type={col.type === 'number' ? 'number' : 'text'} value={editForm[col.id] || ''} onChange={e => setEditForm({...editForm, [col.id]: e.target.value})} style={{ padding:'0.25rem', width: '100%' }} />;
    }

    // Display mode
    let val = row[col.id];
    if (col.id === 'timestamp') {
      const dateStr = val && !val.includes('Z') ? val.replace(' ', 'T') + 'Z' : val;
      return dateStr ? new Date(dateStr).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '-';
    }
    if (col.id === 'date') return val ? new Date(val).toLocaleDateString('fr-FR') : '-';
    if (col.id === 'computed_expense_type_id') {
       return val ? `${val} - ${row.computed_expense_description || ''}` : <span style={{color:'var(--text-secondary)', fontSize:'0.75rem'}}>À définir</span>;
    }
    if (col.type === 'number') return formatEUR(val, row.currency) || '-';
    if (col.type === 'boolean') {
      const icon = val ? <CheckSquare size={16} color="var(--success)" /> : <Square size={16} color="var(--text-secondary)" opacity={0.3} />;
      if (col.id === 'processed') {
        return (
          <span 
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }} 
            onClick={(e) => { e.stopPropagation(); handleQuickToggleProcessed(row.id, val ? 0 : 1); }}
            title={val ? "Marquer comme Non Traité" : "Marquer comme Traité"}
          >
            {icon}
          </span>
        );
      }
      return icon;
    }
    
    if (col.id === 'status') {
       const styleObj = getStatusStyle(val);
       return (
         <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center', flexWrap: 'wrap' }}>
           <span className="badge" style={{ background: styleObj.background, color: styleObj.color }}>{styleObj.label}</span>
         </div>
       );
    }

    return val || '-';
  };

  const renderPagination = () => (
    <div className="pagination">
      <div>
        Affichage de {(page - 1) * limit + (total > 0 ? 1 : 0)} à {Math.min(page * limit, total)} sur {total}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button className="btn-icon" disabled={page === 1} onClick={() => setPage(page - 1)}>&lt;</button>
        <button className="btn-icon" disabled={page * limit >= total} onClick={() => setPage(page + 1)}>&gt;</button>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', height: '100%' }}>
      <div className="header" style={{ marginBottom: '1rem' }}>
        <h1 style={{ fontSize: '1.75rem', fontWeight: 700, margin: '0 0 0.5rem 0' }}>Dépenses</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>Gérez, corrigez et exportez toutes vos dépenses centralisées.</p>
      </div>

      <div className="glass-panel data-table-wrapper" style={{ overflowX: 'auto', display: 'flex', flexDirection: 'column' }}>
        <div className="table-toolbar" style={{ flexWrap: 'wrap' }}>
        <div className="filters-group" style={{ flexWrap: 'wrap' }}>
          <div style={{ position: 'relative' }}>
            <Search size={18} style={{ position: 'absolute', left: '10px', top: '10px', color: 'var(--text-secondary)' }} />
            <input 
              type="text" placeholder="Recherche globale..." className="input-field" 
              style={{ paddingLeft: '2.2rem', width: '250px' }}
              value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {selectedIds.length > 0 && (
              <>
                  <button className="btn" style={{ background: '#10b981', color: 'white', border: 'none', height: '36px', padding: '0 15px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }} onClick={() => handleToggleProcessed(1)} disabled={processing}>
                      <CheckSquare size={16} /> Marquer Traité ({selectedIds.length})
                  </button>
                  <button className="btn" style={{ background: '#f59e0b', color: 'white', border: 'none', height: '36px', padding: '0 15px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }} onClick={() => handleToggleProcessed(0)} disabled={processing}>
                      <Square size={16} /> Non Traité ({selectedIds.length})
                  </button>
              </>
          )}
          <button className="btn btn-danger" style={{ opacity: selectedIds.length === 0 ? 0.5 : 1, height: '36px', padding: '0 15px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', minWidth: '130px', justifyContent: 'center' }} disabled={selectedIds.length === 0} onClick={handleDeleteMany}>
            <Trash2 size={16} /> Supprimer ({selectedIds.length})
          </button>
          <button className="btn" style={{ background: '#475569', color: 'white', border: 'none', height: '36px', padding: '0 15px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', minWidth: '130px', justifyContent: 'center' }} onClick={() => { setHideProcessed(!hideProcessed); setPage(1); }}>
            {!hideProcessed ? <EyeOff size={16} /> : <Eye size={16} />} 
            {!hideProcessed ? 'Masquer traitées' : 'Afficher traitées'}
          </button>
          <button className="btn" style={{ background: '#2563eb', color: 'white', border: 'none', height: '36px', padding: '0 15px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', minWidth: '130px', justifyContent: 'center' }} onClick={handleForceQontoSync} title="Forcer la récupération depuis Qonto">
            <RefreshCw size={16} /> Synchro Qonto
          </button>
          <button className="btn" style={{ background: '#8b5cf6', color: 'white', border: 'none', height: '36px', padding: '0 15px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', minWidth: '130px', justifyContent: 'center' }} onClick={loadData} title="Rafraîchir les données">
            <RefreshCw size={16} /> Actualiser
          </button>
          <button className="btn" style={{ background: 'var(--success)', color: 'white', border: 'none', height: '36px', padding: '0 15px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', minWidth: '130px', justifyContent: 'center' }} onClick={exportExcel}>
            <Download size={16} /> Excel
          </button>
        </div>
      </div>

      {selectedIds.length > 0 && selectedIds.length === data.length && total > data.length && selectedIds.length < total && (
        <div style={{ background: '#e0f2fe', color: '#0369a1', padding: '12px 16px', borderRadius: '8px', margin: '0 0 16px 0', border: '1px solid #7dd3fc', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
          <span>Toutes les <strong>{selectedIds.length}</strong> factures de cette page sont sélectionnées.</span>
          <button className="btn" onClick={handleSelectAllGlobally} style={{ background: '#ffffff', color: '#0369a1', border: '1px solid #bae6fd', fontWeight: '500' }}>
            Sélectionner les {total} factures de cette recherche ?
          </button>
        </div>
      )}

      {renderPagination()}

      <div className="table-container" style={{ overflowX: 'auto', paddingBottom: '1rem' }}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <table style={{ minWidth: 'max-content', width: '100%' }}>
            <thead>
              <SortableContext items={activeCols} strategy={horizontalListSortingStrategy}>
                <tr>
                  {columns.map(col => (
                    <SortableHeader 
                      key={col.id} 
                      col={col} 
                      width={colWidths[col.id] || col.width || '150px'}
                      onResize={(newWidth) => handleResizeColumn(col.id, newWidth)}
                      sortBy={sortBy} 
                      sortDir={sortDir} 
                      filters={filters} 
                      setFilters={setFilters} 
                      handleSort={handleSort}
                      setPage={setPage}
                      allSelected={data.length > 0 && selectedIds.length === data.length}
                      handleSelectAll={handleSelectAll}
                    />
                  ))}
                </tr>
              </SortableContext>
            </thead>
            <tbody>
              {data.map(row => (
                  <tr key={row.id}>
                    {columns.map(col => (
                      <td key={col.id} title={getCellTitle(col, row)} style={{ whiteSpace: 'nowrap', width: colWidths[col.id] || col.width || '150px', minWidth: colWidths[col.id] || col.width || '150px', maxWidth: colWidths[col.id] || col.width || '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {col.id === 'select' && (
                          <input type="checkbox" checked={selectedIds.includes(row.id)} onChange={() => handleSelect(row.id)} />
                        )}
                        {col.id !== 'select' && renderCellContent(col, row, editingId === row.id)}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        </DndContext>
        
        {loading && !isSyncing && (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
            Chargement des données...
          </div>
        )}
        {!loading && data.length === 0 && (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
            Aucune facture trouvée.
          </div>
        )}
      </div>

      {isSyncing && (
        <div className="spinner-overlay">
          <div className="spinner"></div>
          <p style={{ marginTop: '1rem', fontWeight: '500', color: '#0f172a' }}>Synchronisation avec Qonto en cours...</p>
          <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#64748b' }}>Cette opération peut prendre jusqu'à une minute.</p>
        </div>
      )}

      {renderPagination()}

      {jsonViewer && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--surface)', padding: '2rem', borderRadius: '12px', width: '80%', maxWidth: '800px', maxHeight: '80vh', overflowY: 'auto' }}>
            <h3 style={{ marginTop: 0 }}>Données Source SQLite - Import #{jsonViewer.id}</h3>
            <pre style={{ background: '#1e1e1e', color: '#00ff00', padding: '1rem', borderRadius: '8px', fontSize: '0.8rem', overflowX: 'auto', whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(jsonViewer, null, 2)}
            </pre>
            <div style={{ marginTop: '1rem', textAlign: 'right' }}>
              <button className="btn btn-primary" onClick={() => setJsonViewer(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default HistoryTable;
